import React from 'react';
import type { OfferPayment } from '../types';
import { formatMoney as money, resolveOfferFees } from './offerFees';
import { InfoTip } from '../../../ui/Tooltip';
import { feeTips } from '../../../../onboarding/copy';

const LabelWithTip: React.FC<{ children: React.ReactNode; tip: string; className?: string }> = ({
  children,
  tip,
  className,
}) => (
  <span className={`inline-flex items-center gap-1.5 ${className ?? ''}`}>
    {children}
    <InfoTip content={tip} size={12} />
  </span>
);

export interface OfferFeeSummaryProps {
  currency: string;
  gigAmount: number;
  payments?: OfferPayment[];
  /** True when this offer is a direct/standalone offer without an associated gig */
  isDirectOffer?: boolean;
  /**
   * Perspective to display:
   * - 'payer': shows the payer charge breakdown (Base + Processing fee = Total)
   * - 'payee': shows the payee payout breakdown (Base - Commission = Net payout)
   * - 'both': shows both sides (with context-accurate terminology)
   */
  perspective?: 'payer' | 'payee' | 'both';
  /** Optional role to personalize labels (e.g. 'You pay' / 'You receive') */
  viewerRole?: 'payer' | 'payee' | 'third_party';
  /** Legacy prop: when explicitly passed as false, forces hiding the payee section */
  showCandidatePayout?: boolean;
}

export const OfferFeeSummary: React.FC<OfferFeeSummaryProps> = ({
  currency,
  gigAmount,
  payments,
  isDirectOffer = false,
  perspective = 'both',
  viewerRole,
  showCandidatePayout = true,
}) => {
  const fees = resolveOfferFees(gigAmount, payments);

  const showPayerSection = perspective !== 'payee';
  const showPayeeSection = perspective !== 'payer' && showCandidatePayout !== false;

  const baseLabel = isDirectOffer ? 'Offer amount' : 'Gig amount';

  const payerHeader =
    viewerRole === 'payer' ? 'You pay' : isDirectOffer ? 'Client pays' : 'Gig creator pays';
  const payerTotalLabel =
    viewerRole === 'payer'
      ? 'Total you pay'
      : isDirectOffer
        ? 'Total paid by client'
        : 'Total paid by gig creator';

  const payeeHeader =
    viewerRole === 'payee'
      ? 'You receive'
      : isDirectOffer
        ? 'Provider receives'
        : 'Candidate receives';
  const payeePayoutLabel =
    viewerRole === 'payee' ? 'Your payout' : isDirectOffer ? 'Provider payout' : 'Candidate payout';

  const footerText = (() => {
    let text = '';
    if (showPayerSection && !showPayeeSection) {
      text = isDirectOffer
        ? 'The processing fee is paid by the client at checkout.'
        : 'The processing fee is paid by the gig creator at checkout.';
    } else if (!showPayerSection && showPayeeSection) {
      text =
        viewerRole === 'payee'
          ? 'Musicade’s commission is deducted from your payout.'
          : isDirectOffer
            ? 'Musicade’s commission is deducted from the provider payout.'
            : 'Musicade’s commission is deducted from the candidate payout.';
    } else if (showPayerSection && showPayeeSection) {
      text = isDirectOffer
        ? 'The processing fee is paid by the client. Musicade’s commission is deducted from the provider payout.'
        : 'The processing fee is paid by the gig creator. Musicade’s commission is deducted from the candidate payout.';
    }
    if (fees.fromPayment) {
      text += ' These amounts were saved with the payment.';
    }
    return text;
  })();

  return (
    <div className="space-y-4 text-sm">
      {showPayerSection && (
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
            {payerHeader}
          </p>
          <div className="flex justify-between items-center text-zinc-400">
            <span>{baseLabel}</span>
            <span className="font-semibold text-white">{money(fees.gigAmount, currency)}</span>
          </div>
          <div className="flex justify-between items-center text-zinc-400">
            <LabelWithTip tip={feeTips.processingFee}>Processing fee (2.99% + $0.31)</LabelWithTip>
            <span className="font-semibold text-white">{money(fees.processingFee, currency)}</span>
          </div>
          <div className="flex justify-between items-center border-t border-[#22242a] pt-2">
            <LabelWithTip tip={feeTips.creatorTotal} className="font-semibold text-white">
              {payerTotalLabel}
            </LabelWithTip>
            <span className="font-bold text-white">{money(fees.creatorTotal, currency)}</span>
          </div>
        </div>
      )}

      {showPayeeSection && (
        <div className={`space-y-2 ${showPayerSection ? 'border-t border-[#22242a] pt-4' : ''}`}>
          <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
            {payeeHeader}
          </p>
          <div className="flex justify-between items-center text-zinc-400">
            <span>{baseLabel}</span>
            <span className="font-semibold text-white">{money(fees.gigAmount, currency)}</span>
          </div>
          <div className="flex justify-between items-center text-zinc-400">
            <LabelWithTip tip={feeTips.commission}>
              Musicade commission (6.81% + $0.66)
            </LabelWithTip>
            <span className="font-semibold text-red-400">
              -{money(fees.musicadeCommission, currency)}
            </span>
          </div>
          <div className="flex justify-between items-center border-t border-[#22242a] pt-2">
            <LabelWithTip tip={feeTips.payout} className="font-semibold text-white">
              {payeePayoutLabel}
            </LabelWithTip>
            <span className="font-bold text-emerald-400">
              {money(fees.candidatePayout, currency)}
            </span>
          </div>
        </div>
      )}

      {footerText && <p className="text-[11px] leading-relaxed text-zinc-500">{footerText}</p>}
    </div>
  );
};
